import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  caseFuneralHomeAtom,
  objectKeySelector,
  showPinturaAtom,
  itemSelector,
  fhLogoAtom,
  rightMenuAtom,
} from 'atoms'
import {
  toggleQuickActionsForItemIdAtom,
  closeQuickActionsForItemIdAtom,
} from '@/atoms/itemControlAtoms'
import { caseImagesAtom, filledImagesSelector } from '@/atoms/caseImageAtoms'
import { useItemControlState } from '@/hooks/editor/useItemControlState'
import { useRecoilValue, useSetRecoilState, useRecoilState } from 'recoil'
import {
  ImageWrapper,
  CroppedImage,
  ImageContainer,
  ProcessingOverlay,
  AiEditedTag,
} from './PageImage.styled'
import { Icon } from '@/components/priority/Icon/Icon'
import { useTranslation } from 'react-i18next'
import getImageDimensions from 'utils/editor/getImageDimensions'
import { isImagePlaceholder } from 'utils/editor/imagePlaceholder'
import { ImageCropper } from '../ImageCropper/ImageCropper'
import { useCheckImageQuality } from '@/hooks/editor/useCheckImageQuality'
import { useImageQuickActions } from '@/hooks/editor/useImageQuickActions'
import { determineImageUrl } from '@/utils/editor/determineImageUrl'
import { isItemEditedWithAi } from '@/utils/editor/editedWithAi'
import { PAGE_IMAGE_TEST_IDS } from './pageImageTestIds'
import { zLayers } from '@/utils/styling/zLayers'
import {
  IMAGE_QUALITY_BANNER_PPI_THRESHOLD,
  IMAGE_QUALITY_PAPER_PPI_THRESHOLD,
  NON_EDITABLE_IMAGE_SUBTYPES,
} from '@messenger/consts'

export const PageImage = ({
  itemRef,
  image: {
    url,
    editedUrl,
    edits: imageEdits = {},
    aiVariantId,
    aiVariantUrl,
  } = {},
  properties: { subtype, caseImage } = {},
  id,
  multiSelectMode,
  maintainAspectRatio,
  width,
  height,
  type,
  assetLocked,
  selected,
  itemLocked,
  edits = {},
  z,
  environment,
  ...props
}) => {
  const [imgSrc, setImgSrc] = useState(null)
  const { t } = useTranslation()

  const activeMenu = useRecoilValue(rightMenuAtom)
  const fhLogo = useRecoilValue(fhLogoAtom)
  const logo = useRecoilValue(
    objectKeySelector({ atom: caseFuneralHomeAtom, property: 'logo' }),
  )

  // The size an image takes from its file (determineImageDimensions below)
  // is derived on every load, not authored: it is written as an annotation,
  // so a page nobody touched does not open with "Unsaved changes" on account
  // of its images. A person's own resize dirties the page through the
  // handle before this re-derives the height from the aspect ratio.
  const [itemWidth, setItemWidth] = useRecoilState(
    itemSelector({
      atomFamily: type,
      id: id,
      path: 'width',
      annotation: true,
    }),
  )
  const [itemHeight, setItemHeight] = useRecoilState(
    itemSelector({
      atomFamily: type,
      id: id,
      path: 'height',
      annotation: true,
    }),
  )

  const setShowPintura = useSetRecoilState(showPinturaAtom)
  const setCropLeft = useSetRecoilState(
    itemSelector({ atomFamily: type, id: id, path: 'cropLeft' }),
  )
  const setCropRight = useSetRecoilState(
    itemSelector({ atomFamily: type, id: id, path: 'cropRight' }),
  )
  const setCropTop = useSetRecoilState(
    itemSelector({ atomFamily: type, id: id, path: 'cropTop' }),
  )
  const setCropBottom = useSetRecoilState(
    itemSelector({ atomFamily: type, id: id, path: 'cropBottom' }),
  )
  const setEditedImageUrl = useSetRecoilState(
    itemSelector({ atomFamily: type, id: id, path: 'image.editedUrl' }),
  )
  const setImageEdits = useSetRecoilState(
    itemSelector({ atomFamily: type, id: id, path: 'image.edits' }),
  )
  const setAiVariantId = useSetRecoilState(
    itemSelector({ atomFamily: type, id: id, path: 'image.aiVariantId' }),
  )
  const setAiVariantUrl = useSetRecoilState(
    itemSelector({ atomFamily: type, id: id, path: 'image.aiVariantUrl' }),
  )

  const {
    cropLeft = 0,
    cropRight = 0,
    cropTop = 0,
    cropBottom = 0,
  } = props || {}
  const filledImages = useRecoilValue(filledImagesSelector)
  const caseImages = useRecoilValue(caseImagesAtom)
  const isEditorEnvironment = environment?.trim?.() === 'editor'
  const isBackgroundMenu = activeMenu === 'Background'
  const isStylizedVerse = subtype === 'stylizedVerse'
  const croppedWidth = Math.max(0, (itemWidth || 0) - cropLeft - cropRight)
  const croppedHeight = Math.max(0, (itemHeight || 0) - cropTop - cropBottom)
  const imageId = `${type}-${id}`
  const isDesignAsset = subtype === 'designAsset'
  const isImageFitterFlagEnabled =
    process.env.REACT_APP_TMP_IMAGE_FITTER_FLAG === 'true'
  const isNonPhotoItem = NON_EDITABLE_IMAGE_SUBTYPES.includes(subtype)
  const canUseQuickActions = !isNonPhotoItem
  // The "Edited with AI" tag is an editor-only hint (never printed). It shows
  // while the item shows a background removal or an accepted, unreverted
  // enhancement of its photo, the one rule `isItemEditedWithAi` holds.
  const showAiEditedTag =
    isEditorEnvironment &&
    isItemEditedWithAi(caseImages, {
      image: { url, aiVariantId },
      properties: { caseImage },
    })

  const isImageFitterEnabled = useMemo(
    () =>
      !itemLocked && selected && !isStylizedVerse && isImageFitterFlagEnabled,
    [itemLocked, selected, isStylizedVerse, isImageFitterFlagEnabled],
  )

  const showPintura = useCallback(() => setShowPintura(true), [setShowPintura])

  const setImage = useCallback(() => {
    const final = determineImageUrl({
      url,
      editedUrl,
      subtype,
      caseImage,
      filledImages,
      logo,
      fhLogo,
    })

    setImgSrc(final)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filledImages, caseImage, logo, fhLogo, url, subtype, editedUrl, id])

  const determineImageDimensions = useCallback(
    async (width, src) => {
      try {
        if (isImagePlaceholder(src)) return
        const isImageCropped = cropLeft || cropRight || cropTop || cropBottom
        const shouldMaintainAspectRatio = maintainAspectRatio || isImageCropped
        setItemWidth(width)
        if (shouldMaintainAspectRatio) {
          setItemHeight(height)
        } else {
          const imageDimensions = await getImageDimensions(src)
          setItemHeight(
            width * (imageDimensions.height / imageDimensions.width),
          )
        }
      } catch (err) {
        console.error(src, err)
      }
    },
    [
      cropLeft,
      cropRight,
      cropTop,
      cropBottom,
      maintainAspectRatio,
      height,
      setItemWidth,
      setItemHeight,
    ],
  )

  useEffect(() => {
    setImage()
  }, [caseImage, subtype, url, editedUrl, logo, fhLogo, filledImages, setImage])

  useEffect(() => {
    if (!imgSrc) return
    determineImageDimensions(width, imgSrc)
  }, [imgSrc, width, determineImageDimensions])

  const {
    showQuickActions,
    setShowQuickActions,
    isProcessingEdit,
    quickActions,
  } = useImageQuickActions({
    imgSrc,
    imageEdits,
    url,
    aiVariantId,
    aiVariantUrl,
    caseImage,
    subtype,
    filledImages,
    selected,
    setEditedImageUrl,
    setImageEdits,
    setAiVariantId,
    setAiVariantUrl,
    setImgSrc,
  })

  // Listen for toggle quick actions signal from overlay
  const [toggleQuickActionsForItemId, setToggleQuickActionsForItemId] =
    useRecoilState(toggleQuickActionsForItemIdAtom)

  useEffect(() => {
    if (toggleQuickActionsForItemId === id) {
      setShowQuickActions((prev) => !prev)
      // Reset the atom to allow repeated toggles
      setToggleQuickActionsForItemId(null)
    }
  }, [
    toggleQuickActionsForItemId,
    id,
    setShowQuickActions,
    setToggleQuickActionsForItemId,
  ])

  // Listen for close quick actions signal from overlay
  const [closeQuickActionsForItemId, setCloseQuickActionsForItemId] =
    useRecoilState(closeQuickActionsForItemIdAtom)

  useEffect(() => {
    if (closeQuickActionsForItemId === id) {
      setShowQuickActions(false)
      // Reset the atom to allow repeated closes
      setCloseQuickActionsForItemId(null)
    }
  }, [
    closeQuickActionsForItemId,
    id,
    setShowQuickActions,
    setCloseQuickActionsForItemId,
  ])

  const {
    effectivePixelsPerInch,
    originalImagePixelSize: {
      width: originalImageWidth,
      height: originalImageHeight,
    },
    showPrintQualityWarning,
  } = useCheckImageQuality({
    imageId,
    imageUrl: imgSrc,
    displayedBoxWidthPixels: itemWidth,
    displayedBoxHeightPixels: itemHeight,
    cropLeftPixels: cropLeft,
    cropRightPixels: cropRight,
    cropTopPixels: cropTop,
    cropBottomPixels: cropBottom,
    minimumPixelsPerInchBannerProducts: IMAGE_QUALITY_BANNER_PPI_THRESHOLD,
    minimumPixelsPerInchPaperProducts: IMAGE_QUALITY_PAPER_PPI_THRESHOLD,
    currentEnvironment: environment,
    environmentsToWarnIn: ['editor'],
    skipQualityCheck: isDesignAsset,
  })

  // Register image-specific control state with the overlay
  const imageControlState = useMemo(
    () => ({
      image: {
        showPrintQualityWarning,
        effectivePixelsPerInch,
        // Rebuilt from its two numbers so the memo below depends on values,
        // not on an object the quality hook may recreate every render.
        originalImagePixelSize: {
          width: originalImageWidth,
          height: originalImageHeight,
        },
        showQuickActions,
        isProcessingEdit,
        canUseQuickActions,
        quickActions,
        subtype,
      },
    }),
    [
      showPrintQualityWarning,
      effectivePixelsPerInch,
      originalImageWidth,
      originalImageHeight,
      showQuickActions,
      isProcessingEdit,
      canUseQuickActions,
      quickActions,
      subtype,
    ],
  )

  useItemControlState(id, imageControlState)

  const handleImageWrapperClick = useCallback(
    (event) => {
      // Close quick actions if they're open, but don't deselect the image
      if (showQuickActions) {
        event.stopPropagation()
        setShowQuickActions(false)
      }
    },
    [showQuickActions, setShowQuickActions],
  )

  const handleDoubleClick = useCallback(() => {
    const lockedAsset = assetLocked || isBackgroundMenu
    const isPlaceholder = isImagePlaceholder(imgSrc)

    const pinturaDisabled =
      lockedAsset || isStylizedVerse || isPlaceholder || multiSelectMode
    if (pinturaDisabled) return

    showPintura()
  }, [
    assetLocked,
    isBackgroundMenu,
    imgSrc,
    isStylizedVerse,
    showPintura,
    multiSelectMode,
  ])

  const handleCropChange = useCallback(
    (newCrop) => {
      setCropLeft(newCrop.left)
      setCropRight(newCrop.right)
      setCropTop(newCrop.top)
      setCropBottom(newCrop.bottom)
    },
    [setCropLeft, setCropRight, setCropTop, setCropBottom],
  )

  return (
    <ImageContainer
      $isEditorEnvironment={isEditorEnvironment}
      width={itemWidth}
      height={itemHeight}
      edits={edits}
      z={z}
      {...props}
    >
      <ImageWrapper
        ref={itemRef}
        edits={edits}
        width={croppedWidth}
        height={croppedHeight}
        onClick={handleImageWrapperClick}
        onDoubleClick={handleDoubleClick}
        top={cropTop}
        left={cropLeft}
      >
        <CroppedImage
          imgSrc={imgSrc}
          left={cropLeft}
          top={cropTop}
          $cropRight={cropRight}
          $cropBottom={cropBottom}
          width={itemWidth}
          height={itemHeight}
        />
        {isProcessingEdit && (
          <ProcessingOverlay
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            background="rgba(255, 255, 255, 0.8)"
            align="center"
            justify="center"
            zindex={String(zLayers.addOne(z))}
            radius="inherit"
          />
        )}
      </ImageWrapper>
      {showAiEditedTag && (
        <AiEditedTag
          data-testid={PAGE_IMAGE_TEST_IDS.AI_TAG}
          $zIndex={zLayers.addOne(z)}
        >
          <Icon icon="sparkles" fill="#FFFFFF" width="10px" />
          {t('editor.canvas.editedWithAi')}
        </AiEditedTag>
      )}
      {isImageFitterEnabled && (
        <ImageCropper
          selected={selected}
          width={itemWidth}
          height={itemHeight}
          cropLeft={cropLeft}
          cropRight={cropRight}
          cropTop={cropTop}
          cropBottom={cropBottom}
          onCropChange={handleCropChange}
        />
      )}
    </ImageContainer>
  )
}

export default PageImage
